import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  Input,
  output,
  ViewChild,
} from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { concatMap, first } from 'rxjs';

import { runtimeSelectors } from '@blaze-runtime/engine-data-access';
import { BlazeAccountFacade } from '@blaze-shared/app-state-data-access';
import { urlUtils } from '@blaze-shared/platform-util';
import { FileResult, UploaderFacade, UploaderOptions } from '@blaze-shared/uploader';

import { FileService } from '../../../../services/file.service';
import {
  CellValueUpdateModelForFile,
  type InlineEditorConfig,
  type InlineEditorLayoutMode,
} from '../base-editor/inline-editor.types';

@UntilDestroy()
@Component({
  selector: 'be-file-menu',
  templateUrl: './file-menu.component.html',
  styleUrls: ['./file-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TableFileMenuComponent implements AfterViewInit {
  private readonly blazeAccountFacade = inject(BlazeAccountFacade);
  private readonly fileService = inject(FileService);
  private readonly store = inject(Store);
  private readonly uploader = inject(UploaderFacade);

  valueChange = output<unknown>();

  @Input() config?: InlineEditorConfig;
  @Input() layoutMode?: InlineEditorLayoutMode;
  @Input() value: string | number | string[] | null | undefined;

  @ViewChild(MatMenuTrigger) menuTrigger!: MatMenuTrigger | undefined;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  acceptedExtensions = '*.*';
  projectId = '';

  readonly getFileUrlHref = urlUtils.getFileUrlHref;

  get isInputLike(): boolean {
    return this.layoutMode === 'input-like';
  }

  get detailDate(): CellValueUpdateModelForFile | undefined {
    return this.config?.detailData as CellValueUpdateModelForFile;
  }

  get isSecure(): boolean {
    return !!this.detailDate?.isSecure;
  }

  get detailValue(): FileResult | undefined {
    return this.detailDate?.file;
  }

  get isAddAction(): boolean {
    return this.detailDate?.isAddFileAction ?? false;
  }

  get isDownloadAction(): boolean {
    return this.detailDate?.isDownloadFileAction ?? false;
  }

  get isRemoveFileAction(): boolean {
    return this.detailDate?.isRemoveFileAction ?? false;
  }

  get isMultiple(): boolean {
    return this.config?.columnSchema?.multipleValues || false;
  }

  ngAfterViewInit(): void {
    if (this.isAddAction) {
      setTimeout(() => {
        this.fileInput.nativeElement.click();
      });
    } else if (this.isDownloadAction) {
      setTimeout(() => {
        this.downloadSecureFile();
      });
    } else if (this.isRemoveFileAction) {
      setTimeout(() => {
        this.fileRemove();
      });
    } else if (this.layoutMode !== 'input-like') {
      this.menuTrigger?.openMenu();
    }
  }

  downloadSecureFile(event?: Event): void {
    if (!this.isSecure || !this.detailValue?.url) {
      return;
    }

    event?.preventDefault();
    this.fileService.downloadSecureFile(this.detailValue.url);
  }

  fileRemove(event?: Event, file?: FileResult): void {
    if (this.layoutMode === 'input-like') {
      if (this.isMultiple) {
        this.value = (this.value as string[]).filter((value, index) =>
          file ? value !== file.url : index !== 0,
        );
        this.valueChange.emit(this.value);
      } else {
        this.valueChange.emit(null);
      }
    } else {
      this.valueChange.emit({ ...this.detailValue, isRemoveAction: true });
    }
    event?.preventDefault();
  }

  fileSelected(fileInput: EventTarget | null): void {
    const fileInputElement = fileInput as HTMLInputElement;
    const files = Array.from(fileInputElement?.files ?? []);
    fileInputElement.value = '';

    if (!files || files.length === 0) {
      return;
    }
    this.upload(files);
  }

  externalClickAction(
    event: FileResult | ({ event: Event; selected: string } & CellValueUpdateModelForFile),
  ): void {
    if ('event' in event) {
      event.event.preventDefault();
      event.event.stopImmediatePropagation();
    }
    if ((event as CellValueUpdateModelForFile).isAddFileAction) {
      this.startAddFileProcess();
    } else if ((event as CellValueUpdateModelForFile).isRemoveFileAction) {
      this.fileRemove(
        'event' in event ? event.event : undefined,
        (event as CellValueUpdateModelForFile).file,
      );
    } else if ((event as CellValueUpdateModelForFile).file) {
      this.menuTrigger?.openMenu();
    }
  }

  closeEditor(): void {
    this.valueChange.emit(this.value);
  }

  private upload(preselectFiles?: File[]): void {
    const options: UploaderOptions = {
      accountId: this.blazeAccountFacade.getAccountId(),
      preselectFiles,
      acceptedFileTypes: [this.acceptedExtensions],
      isMultipleSelection: this.isMultiple || false,
    };

    // If upload is to secure vault - pass project id and the flag
    if (this.isSecure) {
      options.projectId = this.projectId;
      options.isSecure = true;
    }

    this.store
      .select(runtimeSelectors.selectProjectId)
      .pipe(
        first(),
        concatMap((projectId) => {
          // If upload is to secure vault - pass project id and the flag
          if (this.isSecure) {
            options.projectId = projectId;
            options.isSecure = true;
          }

          return this.uploader.upload(options);
        }),
      )
      .subscribe((results) => {
        if (!results) {
          return;
        }

        let selected: string | string[] = results.map((v) => v?.url).filter(Boolean) as string[];
        if (!this.isMultiple) {
          selected = selected[0];
        }

        if (this.layoutMode === 'input-like') {
          if (this.isMultiple) {
            this.value = [...((this.value as string[]) || []), ...selected];
          } else {
            this.value = selected;
          }
          this.valueChange.emit(this.value);
        } else {
          this.valueChange.emit({ selected, isAddAction: this.isAddAction });
        }
      });
  }

  private startAddFileProcess(): void {
    this.fileInput.nativeElement.click();
  }
}
